import React, { ChangeEvent, useEffect } from 'react'
import {
  CButton,
  CCardBody,
  CCardText,
  CCardTitle,
  CCol,
  CForm,
  CFormInput,
  CImage,
  CRow,
} from '@coreui/react-pro'
import { useStore } from 'effector-react'
import { Divider } from 'antd'
import {
  $imageSettings,
  $imagesSettingCreateStatus,
  getImageSettingsMounted,
  setImageSettingsForm,
  submitCreateImageSettings,
  submitEditImageSettings,
} from './model'
import {
  $demoContest,
  $demoContestCreateStatus,
} from '../DemoContestForm/model'
import { $textsSettingCreateStatus } from '../TextsSettingForm/model'
import { useNavigate } from 'react-router-dom'
import { path } from '../../../../libs'

type Props = {
  mode: 'create' | 'edit'
}

const getPictureSrc = (img: string | Blob) => {
  if (typeof img === 'object') {
    return URL.createObjectURL(img)
  }

  return img
}

export const ImageSettingsForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate()

  const demoContestCreateStatus = useStore($demoContestCreateStatus)
  const textsCreateStatus = useStore($textsSettingCreateStatus)
  const imagesSettingCreateStatus = useStore($imagesSettingCreateStatus)

  const { contestType } = useStore($demoContest)
  const imageSettings = useStore($imageSettings)

  useEffect(() => {
    if (mode === 'edit') {
      getImageSettingsMounted()
    }
  }, [])

  const handleSubmit = () => {
    if (mode === 'create') {
      submitCreateImageSettings({
        ...imageSettings,
        contestId: String(demoContestCreateStatus.id),
      })
    }
    if (mode === 'edit') {
      submitEditImageSettings(imageSettings)
    }
  }

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    let value

    if (event.currentTarget.type === 'file') {
      value = (event.currentTarget as HTMLInputElement).files![0]
    } else {
      value = event.currentTarget.value
    }

    setImageSettingsForm({
      ...imageSettings,
      [event.currentTarget.name]: value,
    })
  }

  useEffect(() => {
    if (mode === 'create') {
      demoContestCreateStatus.isSuccess &&
        imagesSettingCreateStatus.isSuccess &&
        textsCreateStatus.isSuccess &&
        navigate(path.promo.demoContest.entry())
    }
  }, [demoContestCreateStatus, imagesSettingCreateStatus, textsCreateStatus])

  return (
    <CCardBody>
      <CForm>
        <CCardTitle className="mb-3">Content images</CCardTitle>
        <CCardText className="mb-1 fw-bold">Contest card in the list</CCardText>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormInput
              type="file"
              name="cardWeb"
              label="Web"
              onChange={handleChange}
            />
          </CCol>
          {imageSettings?.cardWeb && (
            <CCol sm={2}>
              <CImage
                id="cardWeb"
                src={getPictureSrc(imageSettings.cardWeb)}
                width={150}
                height={150}
              />
            </CCol>
          )}
          <CCol sm={4}>
            <CFormInput
              type="file"
              name="cardApp"
              label="App"
              onChange={handleChange}
            />
          </CCol>
          {imageSettings?.cardApp && (
            <CCol sm={2}>
              <CImage
                id="infoBackgroundApp"
                src={getPictureSrc(imageSettings.cardApp)}
                width={150}
                height={150}
              />
            </CCol>
          )}
        </CRow>
        <CCardText className="mb-1 fw-bold">
          Contest image on the contest page
        </CCardText>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormInput
              type="file"
              name="bannerWeb"
              label="Web"
              onChange={handleChange}
            />
          </CCol>
          {imageSettings?.bannerWeb && (
            <CCol sm={2}>
              <CImage
                id="bannerWeb"
                src={getPictureSrc(imageSettings.bannerWeb)}
                width={150}
                height={150}
              />
            </CCol>
          )}
          <CCol sm={4}>
            <CFormInput
              type="file"
              name="bannerApp"
              label="App"
              onChange={handleChange}
            />
          </CCol>
          {imageSettings?.bannerApp && (
            <CCol sm={2}>
              <CImage
                id="bannerApp"
                src={getPictureSrc(imageSettings.bannerApp)}
                width={150}
                height={150}
              />
            </CCol>
          )}
        </CRow>
        <CCardText className="mb-1 fw-bold">
          Background for top winners banner
        </CCardText>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormInput
              type="file"
              name="winnersInfoBackgroundWeb"
              label="Web"
              onChange={handleChange}
            />
          </CCol>
          {imageSettings?.winnersInfoBackgroundWeb && (
            <CCol sm={2}>
              <CImage
                id="winnersInfoBackgroundWeb"
                src={getPictureSrc(imageSettings.winnersInfoBackgroundWeb)}
                width={150}
                height={150}
              />
            </CCol>
          )}
          <CCol sm={4}>
            <CFormInput
              type="file"
              name="winnersInfoBackgroundApp"
              label="App"
              onChange={handleChange}
            />
          </CCol>
          {imageSettings?.winnersInfoBackgroundApp && (
            <CCol sm={2}>
              <CImage
                id="winnersInfoBackgroundApp"
                src={getPictureSrc(imageSettings.winnersInfoBackgroundApp)}
                width={150}
                height={150}
              />
            </CCol>
          )}
        </CRow>
        {contestType === 'event' && (
          <>
            <Divider />
            <CCardTitle className="mb-3">Event images</CCardTitle>
            <CCardText className="mb-1 fw-bold">
              Background for team bonus banner
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="infoBackgroundWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.infoBackgroundWeb && (
                <CCol sm={2}>
                  <CImage
                    id="infoBackgroundWeb"
                    src={getPictureSrc(imageSettings.infoBackgroundWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="infoBackgroundApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.infoBackgroundApp && (
                <CCol sm={2}>
                  <CImage
                    id="infoBackgroundApp"
                    src={getPictureSrc(imageSettings.infoBackgroundApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">VS</CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="vs"
                  label="All"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.vs && (
                <CCol sm={2}>
                  <CImage
                    id="vs"
                    src={getPictureSrc(imageSettings.vs)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Background for big team banner
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="backgroundBigWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.backgroundBigWeb && (
                <CCol sm={2}>
                  <CImage
                    id="backgroundBigWeb"
                    src={getPictureSrc(imageSettings.backgroundBigWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="backgroundBigApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.backgroundBigApp && (
                <CCol sm={2}>
                  <CImage
                    id="backgroundBigApp"
                    src={getPictureSrc(imageSettings.backgroundBigApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Background for small team banner (with percent)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="backgroundMiddleWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.backgroundMiddleWeb && (
                <CCol sm={2}>
                  <CImage
                    id="backgroundMiddleWeb"
                    src={getPictureSrc(imageSettings.backgroundMiddleWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="backgroundMiddleApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.backgroundMiddleApp && (
                <CCol sm={2}>
                  <CImage
                    id="backgroundMiddleApp"
                    src={getPictureSrc(imageSettings.backgroundMiddleApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 1: Big color avatar (big banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarBigOriginWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarBigOriginWeb && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarBigOriginWeb"
                    src={getPictureSrc(imageSettings.firstAvatarBigOriginWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarBigOriginApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarBigOriginApp && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarBigOriginApp"
                    src={getPictureSrc(imageSettings.firstAvatarBigOriginApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 1: Big grey avatar (big banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarBigGrayWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarBigGrayWeb && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarBigGrayWeb"
                    src={getPictureSrc(imageSettings.firstAvatarBigGrayWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarBigGrayApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarBigGrayApp && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarBigGrayApp"
                    src={getPictureSrc(imageSettings.firstAvatarBigGrayApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 1: Middle color avatar (small banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarMiddleOriginWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarMiddleOriginWeb && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarMiddleOriginWeb"
                    src={getPictureSrc(
                      imageSettings.firstAvatarMiddleOriginWeb,
                    )}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarMiddleOriginApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarMiddleOriginApp && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarMiddleOriginApp"
                    src={getPictureSrc(
                      imageSettings.firstAvatarMiddleOriginApp,
                    )}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 1: Middle grey avatar (small banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarMiddleGrayWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarMiddleGrayWeb && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarMiddleGrayWeb"
                    src={getPictureSrc(imageSettings.firstAvatarMiddleGrayWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarMiddleGrayApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarMiddleGrayApp && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarMiddleGrayApp"
                    src={getPictureSrc(imageSettings.firstAvatarMiddleGrayApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 1: Small avatar (top winner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="firstAvatarSmallOrigin"
                  label="All"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.firstAvatarSmallOrigin && (
                <CCol sm={2}>
                  <CImage
                    id="firstAvatarSmallOrigin"
                    src={getPictureSrc(imageSettings.firstAvatarSmallOrigin)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 2: Big color avatar (big banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarBigOriginWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarBigOriginWeb && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarBigOriginWeb"
                    src={getPictureSrc(imageSettings.secondAvatarBigOriginWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarBigOriginApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarBigOriginApp && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarBigOriginApp"
                    src={getPictureSrc(imageSettings.secondAvatarBigOriginApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 2: Big grey avatar (big banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarBigGrayWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarBigGrayWeb && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarBigGrayWeb"
                    src={getPictureSrc(imageSettings.secondAvatarBigGrayWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  id="imageMedium"
                  name="secondAvatarBigGrayApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarBigGrayApp && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarBigGrayApp"
                    src={getPictureSrc(imageSettings.secondAvatarBigGrayApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 2: Middle color avatar (small banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarMiddleOriginWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarMiddleOriginWeb && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarMiddleOriginWeb"
                    src={getPictureSrc(
                      imageSettings.secondAvatarMiddleOriginWeb,
                    )}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarMiddleOriginApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarMiddleOriginApp && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarMiddleOriginApp"
                    src={getPictureSrc(
                      imageSettings.secondAvatarMiddleOriginApp,
                    )}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 2: Middle grey avatar (small banner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarMiddleGrayWeb"
                  label="Web"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarMiddleGrayWeb && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarMiddleGrayWeb"
                    src={getPictureSrc(imageSettings.secondAvatarMiddleGrayWeb)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarMiddleGrayApp"
                  label="App"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarMiddleGrayApp && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarMiddleGrayApp"
                    src={getPictureSrc(imageSettings.secondAvatarMiddleGrayApp)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
            <CCardText className="mb-1 fw-bold">
              Team 2: Small avatar (top winner)
            </CCardText>
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="file"
                  name="secondAvatarSmallOrigin"
                  label="All"
                  onChange={handleChange}
                />
              </CCol>
              {imageSettings?.secondAvatarSmallOrigin && (
                <CCol sm={2}>
                  <CImage
                    id="secondAvatarSmallOrigin"
                    src={getPictureSrc(imageSettings.secondAvatarSmallOrigin)}
                    width={150}
                    height={150}
                  />
                </CCol>
              )}
            </CRow>
          </>
        )}
        <CButton className="mb-3" onClick={handleSubmit}>
          Create
        </CButton>
      </CForm>
    </CCardBody>
  )
}
